// ========================================
// Message Service - Handles internal messaging data
// ========================================

const MessageService = {
    getAllMessages() {
        return DataManager.getAll(DataManager.KEYS.MESSAGES);
    },

    saveAllMessages(messages) {
        DataManager.saveAll(DataManager.KEYS.MESSAGES, messages);
    },

    getMessage(id) {
        const messages = this.getAllMessages();
        return messages.find(m => m.id === id);
    },

    getMessagesForUser(userId) {
        this.purgeExpiredTrash();
        const messages = this.getAllMessages();
        // Return messages where user is sender or receiver and not deleted
        return messages.filter(m => (m.senderId === userId || m.receiverId === userId) && (!Array.isArray(m.deletedFor) || !m.deletedFor.includes(userId)));
    },

    getUnreadCount(userId) {
        const messages = this.getAllMessages();
        return messages.filter(m => {
            if (m.receiverId !== userId || m.read) return false;
            const isTrashed = (Array.isArray(m.trashFor) && m.trashFor.includes(userId)) || (!m.trashFor && m.trash);
            const isDeleted = Array.isArray(m.deletedFor) && m.deletedFor.includes(userId);
            return !isTrashed && !isDeleted;
        }).length;
    },

    async sendMessage(data) {
        if (data.attachments?.length) throw new Error('Los adjuntos de mensajes aún no tienen almacenamiento seguro. Envía el texto sin adjuntos.');
        const response = await fetch('/api/messages', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify(data) });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'No se pudo enviar el mensaje');
        const updated = [...this.getAllMessages().filter(m => m.id !== result.message.id), result.message];
        FileStorage.dataStore.messages = updated;
        BrowserState.setItem(DataManager.KEYS.MESSAGES, JSON.stringify(updated));
        return result.message;
    },



    markAsRead(messageId) {
        const messages = this.getAllMessages();
        const index = messages.findIndex(m => m.id === messageId);
        if (index !== -1) {
            messages[index].read = true;
            this.saveAllMessages(messages);
            return true;
        }
        return false;
    },

    moveToTrash(id, username) {
        const messages = this.getAllMessages();
        const msg = messages.find(m => m.id === id);
        if (!msg) return false;

        const user = username || (typeof AuthManager !== 'undefined' && AuthManager.getCurrentUser()?.username);
        if (!user) return false;

        if (!Array.isArray(msg.trashFor)) {
            msg.trashFor = msg.trash ? [msg.receiverId] : [];
        }
        if (!msg.trashFor.includes(user)) {
            msg.trashFor.push(user);
        }
        msg.trash = true;

        if (!msg.trashedDates || typeof msg.trashedDates !== 'object') {
            msg.trashedDates = {};
        }
        msg.trashedDates[user] = new Date().toISOString();

        this.saveAllMessages(messages);
        return true;
    },

    restoreMessage(id, username) {
        const messages = this.getAllMessages();
        const msg = messages.find(m => m.id === id);
        if (!msg) return false;

        const user = username || (typeof AuthManager !== 'undefined' && AuthManager.getCurrentUser()?.username);
        if (!user) return false;

        if (Array.isArray(msg.trashFor)) {
            msg.trashFor = msg.trashFor.filter(u => u !== user);
            if (msg.trashFor.length === 0) {
                msg.trash = false;
            }
        } else {
            msg.trash = false;
        }

        if (msg.trashedDates && typeof msg.trashedDates === 'object') {
            delete msg.trashedDates[user];
        }

        this.saveAllMessages(messages);
        return true;
    },

    purgeExpiredTrash() { /* Server owns retention; browsing never purges other users. */ },


    permanentDelete(id, username) {
        const messages = this.getAllMessages();
        const msgIndex = messages.findIndex(m => m.id === id);
        if (msgIndex === -1) return false;

        const user = username || (typeof AuthManager !== 'undefined' && AuthManager.getCurrentUser()?.username);

        const msg = messages[msgIndex];
        if (!Array.isArray(msg.deletedFor)) {
            msg.deletedFor = [];
        }
        if (user && !msg.deletedFor.includes(user)) {
            msg.deletedFor.push(user);
        }

        // Personal deletion is persisted without dropping a shared conversation.

        this.saveAllMessages(messages);
        return true;
    },

    deleteMessage(id, username) {
        // Enviar a papelera por defecto
        return this.moveToTrash(id, username);
    },

    emptyTrash(username) {
        const user = username || (typeof AuthManager !== 'undefined' && AuthManager.getCurrentUser()?.username);
        if (!user) return false;

        const messages = this.getAllMessages();
        let changed = false;

        for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            const isTrashed = (Array.isArray(msg.trashFor) && msg.trashFor.includes(user)) || (!msg.trashFor && msg.trash);
            if (isTrashed && (msg.senderId === user || msg.receiverId === user)) {
                if (!Array.isArray(msg.deletedFor)) msg.deletedFor = [];
                if (!msg.deletedFor.includes(user)) msg.deletedFor.push(user);
                msg.trashFor = (msg.trashFor || []).filter(u => u !== user);
                if (msg.trashFor.length === 0) msg.trash = false;

                // Preserve the row so only this user's deletion is synchronized.
                changed = true;
            }
        }

        if (changed) {
            this.saveAllMessages(messages);
            return true;
        }
        return false;
    }
};

// Make MessageService globally available
window.MessageService = MessageService;
